import {
  ComponentType,
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';

import { FeatureFlaggedProps as BackstageFeatureFlaggedProps } from '@backstage/core-app-api';

import { Value } from '@spotify-confidence/sdk';

import { interceptFlaggedContent } from '../../interceptors';
import { isStruct } from '../../utils';
import { useFlagResolver } from '../FlagEvaluationProvider';

const useFlaggedContent = interceptFlaggedContent();

const useStableValue = <T extends Value>(value: T): T => {
  const stable = useRef(value);
  if (stable.current === value) {
    return stable.current;
  }
  if (typeof value !== 'object' || !Value.equal(stable.current, value)) {
    stable.current = value;
  }
  return stable.current;
};

export const useFeatureFlag = <T extends Value>(
  flag: string,
  defaultValue: T,
): T => {
  const useResolvedFlagValue = useFlagResolver();
  const stableDefaultValue = useStableValue(defaultValue);
  return useResolvedFlagValue(flag, stableDefaultValue);
};

type NamedFeatureFlaggedProps = PropsWithChildren<{ name: string }>;

export type FeatureFlaggedProps =
  | BackstageFeatureFlaggedProps
  | NamedFeatureFlaggedProps;

const isNamed = (
  props: FeatureFlaggedProps,
): props is NamedFeatureFlaggedProps => 'name' in props;

const flagOf = (props: FeatureFlaggedProps): string => {
  if (isNamed(props)) {
    return props.name;
  }
  return 'with' in props ? props.with : props.without;
};

const isGateOpen = (value: Value): boolean =>
  value === true || (isStruct(value) && value.enabled === true);

const CLOSED_GATE_STRUCT: Value.Struct = Object.freeze({ enabled: false });

const useFlagGate = (flag: string): boolean => {
  const value = useFeatureFlag<Value>(
    flag,
    flag.includes('.') ? false : CLOSED_GATE_STRUCT,
  );
  return isGateOpen(value);
};

// Undefined outside a named <FeatureFlagged>, which is how On and Off tell
// they have no root to read a flag from.
const FlagEnabledContext = createContext<boolean | undefined>(undefined);

const useFlagEnabled = (branch: 'On' | 'Off'): boolean => {
  const enabled = useContext(FlagEnabledContext);
  if (enabled === undefined) {
    throw new Error(
      `<FeatureFlagged.${branch}> must be inside <FeatureFlagged name="...">.`,
    );
  }
  return enabled;
};

const On = ({ children }: PropsWithChildren) => (
  <>{useFlagEnabled('On') ? children : null}</>
);
On.displayName = 'FeatureFlagged.On';

const Off = ({ children }: PropsWithChildren) => (
  <>{useFlagEnabled('Off') ? null : children}</>
);
Off.displayName = 'FeatureFlagged.Off';

/**
 * Renders children based on a feature flag, in one of two forms.
 *
 * One branch takes the flag inline:
 *
 *     <FeatureFlagged with="my-flag">…</FeatureFlagged>
 *     <FeatureFlagged without="my-flag">…</FeatureFlagged>
 *
 * Two alternative branches name the flag once and split on it, so the flag
 * cannot drift between them:
 *
 *     <FeatureFlagged name="my-flag">
 *       <FeatureFlagged.On>…</FeatureFlagged.On>
 *       <FeatureFlagged.Off>…</FeatureFlagged.Off>
 *     </FeatureFlagged>
 */
const FeatureFlaggedRoot = (props: FeatureFlaggedProps) => {
  const flag = flagOf(props);
  const enabled = useFlagGate(flag);
  const rendersWhenEnabled = 'with' in props;

  const content = isNamed(props) ? (
    <FlagEnabledContext.Provider value={enabled}>
      {props.children}
    </FlagEnabledContext.Provider>
  ) : (
    <>{enabled === rendersWhenEnabled ? props.children : null}</>
  );

  return <>{useFlaggedContent(content, flag, enabled)}</>;
};

export const FeatureFlagged = Object.assign(FeatureFlaggedRoot, { On, Off });

export const splitComponent = <
  V extends Value.Struct,
  P extends Record<string, unknown> = Record<string, unknown>,
>(
  flag: string,
  defaultValue: V,
  factory: (value: V) => ComponentType<P>,
) => {
  const SplitComponent = (props: P) => {
    const value = useFeatureFlag(flag, defaultValue);
    const Component = useMemo(
      () => (value ? factory(value as V) : null),
      [value],
    );

    const content = Component ? <Component {...props} /> : null;

    return <>{useFlaggedContent(content, flag, isGateOpen(value))}</>;
  };
  SplitComponent.displayName = `splitComponent(${flag})`;
  return SplitComponent;
};
